/**
 * v3.admin.pilotOfficers — PILOT-GATE-INFRA admin write surface
 * (Bill 2026-04-25 binding pilot-gating rule).
 *
 * Two-layer gate on the server:
 *   Layer 1 — SystemSettings.pilot.mode toggle (true at v3.0).
 *   Layer 2 — explicit (OfficerCode × FeatureCode) opt-in rows in
 *             dbo.PilotOfficers — managed via these routes.
 *
 * Routes (server: PCBJxChangeApi/Functions/AdminPilotOfficersFunction.cs):
 *   GET    /v3/admin/pilot-officers
 *   POST   /v3/admin/pilot-officers/upsert
 *   DELETE /v3/admin/pilot-officers/{officerCode}/{featureCode}   (soft-delete)
 *   POST   /v3/admin/pilot-officers/bulk-enroll
 *
 * All write routes require the `x-config-seed-bypass: <bypassKey>` header
 * (mirrors the Wave-6.1 / 6.2 admin sub-clients in v3-admin.ts and
 * v3-admin-cart-retention.ts). Wave-13 SUPER-ADMIN-ROLE pass replaces this
 * with role-claim auth.
 *
 * Composed onto `AdminApi.pilotOfficers` from `client.ts` after
 * construction (no ctor change in v3-admin.ts; same isolation pattern as
 * the products + cart-retention sub-clients).
 */

import type {
  RequestOptions,
  PilotFeatureCode,
  PilotOfficer,
  PilotOfficerUpsertInput,
  PilotOfficerBulkEnrollInput,
  ApiResult,
} from '@retail-ops/shared-types';
import type { ApiHttpClient } from '../http.js';
import { buildAdminAuthHeaders } from '../admin-auth.js';
import type { AdminWriteResult } from './admin.js';

class PilotOfficersAdminApi {
  public constructor(private readonly http: ApiHttpClient) {}

  /**
   * List the full pilot enrolment catalog. The read endpoint is admin-scoped
   * but does not require the bypass key.
   */
  public list(opts?: RequestOptions): Promise<ApiResult<PilotOfficer[]>> {
    return this.http.get<ApiResult<PilotOfficer[]>>('/v3/admin/pilot-officers', { ...opts });
  }

  /**
   * Upsert a single (officerCode × featureCode) row. On insert, server
   * stamps EnrolledAt + EnrolledBy from the actor header. On update, only
   * IsActive + Notes (when supplied) are touched — EnrolledAt is preserved
   * for audit.
   */
  public upsert(
    bypassKey: string,
    body: PilotOfficerUpsertInput,
    opts?: RequestOptions,
  ): Promise<AdminWriteResult> {
    const headers = buildAdminAuthHeaders(opts?.headers, bypassKey);
    return this.http.post<AdminWriteResult>('/v3/admin/pilot-officers/upsert', {
      ...opts,
      headers,
      body,
    });
  }

  /**
   * Soft-delete the pair via IsActive=false. The row stays in the table so
   * the audit trail (EnrolledAt / EnrolledBy / original Notes) survives.
   */
  public remove(
    bypassKey: string,
    officerCode: string,
    featureCode: PilotFeatureCode,
    opts?: RequestOptions,
  ): Promise<AdminWriteResult> {
    const headers = buildAdminAuthHeaders(opts?.headers, bypassKey);
    const path =
      `/v3/admin/pilot-officers/${encodeURIComponent(officerCode)}/${encodeURIComponent(featureCode)}`;
    return this.http.del<AdminWriteResult>(path, { ...opts, headers });
  }

  /**
   * Bulk enrol — accepts either `{ officerCodes, featureCode }` (many
   * officers, one feature) or `{ officerCode, featureCodes }` (one
   * officer, many features). Both variants return
   * `{ created, updated, totalPairs }` so the editor can toast accurately.
   */
  public bulkEnroll(
    bypassKey: string,
    body: PilotOfficerBulkEnrollInput,
    opts?: RequestOptions,
  ): Promise<AdminWriteResult> {
    const headers = buildAdminAuthHeaders(opts?.headers, bypassKey);
    return this.http.post<AdminWriteResult>('/v3/admin/pilot-officers/bulk-enroll', {
      ...opts,
      headers,
      body,
    });
  }
}

/**
 * Composite — exposed at `client.retail.admin.pilotOfficers`.
 */
export class AdminPilotOfficersApi {
  public readonly officers: PilotOfficersAdminApi;

  public constructor(http: ApiHttpClient) {
    this.officers = new PilotOfficersAdminApi(http);
  }

  // Convenience pass-throughs so editors can call
  // `client.retail.admin.pilotOfficers.list()` without tunneling through `.officers`.
  public list(opts?: RequestOptions): ReturnType<PilotOfficersAdminApi['list']> {
    return this.officers.list(opts);
  }
  public upsert(
    bypassKey: string,
    body: PilotOfficerUpsertInput,
    opts?: RequestOptions,
  ): ReturnType<PilotOfficersAdminApi['upsert']> {
    return this.officers.upsert(bypassKey, body, opts);
  }
  public remove(
    bypassKey: string,
    officerCode: string,
    featureCode: PilotFeatureCode,
    opts?: RequestOptions,
  ): ReturnType<PilotOfficersAdminApi['remove']> {
    return this.officers.remove(bypassKey, officerCode, featureCode, opts);
  }
  public bulkEnroll(
    bypassKey: string,
    body: PilotOfficerBulkEnrollInput,
    opts?: RequestOptions,
  ): ReturnType<PilotOfficersAdminApi['bulkEnroll']> {
    return this.officers.bulkEnroll(bypassKey, body, opts);
  }
}

// ─────────────────────────────────────────────────────────────────────────
// Module augmentation — extend AdminApi with `.pilotOfficers`.
// ─────────────────────────────────────────────────────────────────────────

declare module './admin.js' {
  // eslint-disable-next-line @typescript-eslint/no-shadow
  interface AdminApi {
    /** PILOT-GATE-INFRA — pilot enrolment matrix. */
    readonly pilotOfficers: AdminPilotOfficersApi;
  }
}
